import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, User, Mail, Phone, MapPin, AlertCircle, CheckCircle } from 'lucide-react';

const BookingCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const recaptchaRef = useRef(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  
  // reCAPTCHA Site Key
  const RECAPTCHA_SITE_KEY = '6LcYdtQrAAAAAEZHyXTBper2_arLZE6jCITbBy4i';
  
  const [formData, setFormData] = useState({
    eventName: '',
    fieldType: 'rugby',
    dates: [],
    startTime: '',
    endTime: '',
    customerName: '',
    email: '',
    phone: '',
    popiAccepted: false
  });

  // Load reCAPTCHA script
  useEffect(() => {
    const loadRecaptcha = () => {
      if (window.grecaptcha) {
        setRecaptchaLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setRecaptchaLoaded(true);
        // Initialize reCAPTCHA widget after script loads
        if (window.grecaptcha && recaptchaRef.current) {
          window.grecaptcha.render(recaptchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
            theme: 'light'
          });
        }
      };
      document.body.appendChild(script);
    };

    loadRecaptcha();
  }, []);

  // Sample bookings data - in real app this would come from API
  useEffect(() => {
    fetchBookings();
  }, [currentDate]);

  const fetchBookings = async () => {
    try {
      // In real app, this would be an API call
      const sampleBookings = [
        {
          id: 1,
          event_name: 'Weekly Rugby Training',
          field_type: 'rugby',
          booking_date: '2025-09-25',
          start_time: '18:00',
          end_time: '20:00',
          customer_name: 'John Smith'
        },
        {
          id: 2,
          event_name: 'Football Match',
          field_type: 'sports',
          booking_date: '2025-09-28',
          start_time: '15:00',
          end_time: '17:00',
          customer_name: 'City FC'
        },
        {
          id: 3,
          event_name: 'Corporate Event',
          field_type: 'both',
          booking_date: '2025-09-30',
          start_time: '09:00',
          end_time: '12:00',
          customer_name: 'ABC Company'
        }
      ];
      setBookings(sampleBookings);
    } catch (error) {
      showMessage('error', 'Failed to load bookings');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const formatDate = (date, day) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${month}-${dayStr}`;
  };

  const getBookingsForDate = (dateStr) => {
    return bookings.filter(booking => booking.booking_date === dateStr);
  };

  const isPastDate = (date, day) => {
    const selectedDate = new Date(date.getFullYear(), date.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate < today;
  };

  const handleDateClick = (day) => {
    if (!day || isPastDate(currentDate, day)) return;
    
    const dateStr = formatDate(currentDate, day);
    const dayBookings = getBookingsForDate(dateStr);
    
    // Check if all fields are booked
    const hasRugbyBooking = dayBookings.some(b => b.field_type === 'rugby' || b.field_type === 'both');
    const hasSportsBooking = dayBookings.some(b => b.field_type === 'sports' || b.field_type === 'both');
    const hasBothBooking = dayBookings.some(b => b.field_type === 'both');
    
    if (hasBothBooking) {
      showMessage('error', 'Both fields are already booked for this date');
      return;
    }
    
    setSelectedDate(dateStr);
    setFormData(prev => ({ 
      ...prev, 
      dates: [dateStr],
      fieldType: hasRugbyBooking ? 'sports' : hasSportsBooking ? 'rugby' : 'rugby'
    }));
    setShowBookingForm(true);
  };

  const validateForm = () => {
    const { eventName, startTime, endTime, customerName, email, phone, popiAccepted } = formData;
    
    if (!eventName.trim()) {
      showMessage('error', 'Event name is required');
      return false;
    }
    
    if (!startTime || !endTime) {
      showMessage('error', 'Start and end times are required');
      return false;
    }
    
    if (startTime >= endTime) {
      showMessage('error', 'End time must be after start time');
      return false;
    }
    
    if (!customerName.trim()) {
      showMessage('error', 'Customer name is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email)) {
      showMessage('error', 'Valid email address is required');
      return false;
    }
    
    const phoneRegex = /^[\+]?[\d\s\-\(\)]+$/;
    if (!phone.trim() || !phoneRegex.test(phone)) {
      showMessage('error', 'Valid phone number is required');
      return false;
    }
    
    if (!popiAccepted) {
      showMessage('error', 'You must accept the POPI terms to continue');
      return false;
    }
    
    return true;
  };

  const checkTimeConflict = () => {
    const selectedDateBookings = getBookingsForDate(selectedDate);
    const { startTime, endTime, fieldType } = formData;
    
    for (let booking of selectedDateBookings) {
      // Check if there's a field conflict
      const fieldConflict = 
        (fieldType === 'both') ||
        (booking.field_type === 'both') ||
        (fieldType === booking.field_type);
      
      if (fieldConflict) {
        // Check time overlap
        const bookingStart = booking.start_time;
        const bookingEnd = booking.end_time;
        
        const timeOverlap = 
          (startTime >= bookingStart && startTime < bookingEnd) ||
          (endTime > bookingStart && endTime <= bookingEnd) ||
          (startTime <= bookingStart && endTime >= bookingEnd);
        
        if (timeOverlap) {
          return booking;
        }
      }
    }
    return null;
  };

  const handleFormSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    const conflictingBooking = checkTimeConflict();
    if (conflictingBooking) {
      showMessage('error', `Time slot conflicts with "${conflictingBooking.event_name}"`);
      return;
    }
    
    // Verify reCAPTCHA
    if (recaptchaLoaded && window.grecaptcha) {
      const recaptchaValue = window.grecaptcha.getResponse(recaptchaRef.current);
      if (!recaptchaValue) {
        showMessage('error', 'Please complete the reCAPTCHA verification');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      // Here you would normally send to your backend API
      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventName: formData.eventName,
          fieldType: formData.fieldType,
          bookingDate: formData.dates[0],
          startTime: formData.startTime,
          endTime: formData.endTime,
          customerName: formData.customerName,
          email: formData.email,
          phone: formData.phone,
          popiAccepted: formData.popiAccepted,
          recaptchaToken: window.grecaptcha ? window.grecaptcha.getResponse() : null
        })
      });
      
      if (!response.ok) {
        throw new Error('Booking failed');
      }
      
      // Add to local state for demo
      const newBooking = {
        id: bookings.length + 1,
        event_name: formData.eventName,
        field_type: formData.fieldType,
        booking_date: formData.dates[0],
        start_time: formData.startTime,
        end_time: formData.endTime,
        customer_name: formData.customerName
      };
      
      setBookings(prev => [...prev, newBooking]);
      setShowBookingForm(false);
      setFormData({
        eventName: '',
        fieldType: 'rugby',
        dates: [],
        startTime: '',
        endTime: '',
        customerName: '',
        email: '',
        phone: '',
        popiAccepted: false
      });
      
      showMessage('success', 'Booking created successfully!');
      
      // Reset reCAPTCHA
      if (window.grecaptcha && recaptchaRef.current) {
        window.grecaptcha.reset();
      }
      
    } catch (error) {
      console.error('Booking error:', error);
      showMessage('error', 'Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getAvailableFieldTypes = () => {
    if (!selectedDate) return ['rugby', 'sports', 'both'];
    
    const dayBookings = getBookingsForDate(selectedDate);
    const hasRugbyBooking = dayBookings.some(b => b.field_type === 'rugby' || b.field_type === 'both');
    const hasSportsBooking = dayBookings.some(b => b.field_type === 'sports' || b.field_type === 'both');
    
    if (hasRugbyBooking && hasSportsBooking) return [];
    if (hasRugbyBooking) return ['sports'];
    if (hasSportsBooking) return ['rugby'];
    
    return ['rugby', 'sports', 'both'];
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 shadow-lg ${
            message.type === 'success' 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle size={24} /> : <AlertCircle size={24} />}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* Header Section */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-4">
            Scottburgh Fields
          </h1>
          <p className="text-xl text-gray-600 mb-2">Rugby & Sports Field Booking</p>
          <p className="text-gray-500">Select a date to make your booking</p>
        </div>

        {/* Calendar Container */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
          {/* Calendar Header */}
          <div className="bg-gradient-to-r from-blue-600 to-green-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-3 hover:bg-white hover:bg-opacity-20 rounded-xl transition-all duration-200 text-white"
              >
                <ChevronLeft size={28} />
              </button>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-3 hover:bg-white hover:bg-opacity-20 rounded-xl transition-all duration-200 text-white"
              >
                <ChevronRight size={28} />
              </button>
            </div>
          </div>

          {/* Calendar Body */}
          <div className="p-6 md:p-8">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-2 md:gap-4 mb-4">
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                <div key={day} className="p-4 text-center">
                  <div className="font-bold text-gray-700 text-sm md:text-base">
                    <span className="hidden md:inline">{day}</span>
                    <span className="md:hidden">{day.slice(0, 3)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-2 md:gap-4">
              {days.map((day, index) => {
                if (!day) return <div key={index} className="aspect-square"></div>;
                
                const dateStr = formatDate(currentDate, day);
                const dayBookings = getBookingsForDate(dateStr);
                const isPast = isPastDate(currentDate, day);
                const hasBookings = dayBookings.length > 0;
                const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
                
                return (
                  <div
                    key={day}
                    onClick={() => handleDateClick(day)}
                    className={`
                      aspect-square p-2 md:p-4 flex flex-col items-center justify-center
                      rounded-xl border-2 transition-all duration-200 cursor-pointer
                      relative overflow-hidden group
                      ${isPast 
                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200' 
                        : hasBookings
                        ? 'bg-gradient-to-br from-blue-100 to-blue-200 text-blue-800 border-blue-300 hover:from-blue-200 hover:to-blue-300 shadow-md'
                        : isToday
                        ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-800 border-green-300 hover:from-green-200 hover:to-green-300 shadow-md'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gradient-to-br hover:from-gray-50 hover:to-gray-100 hover:border-gray-300 hover:shadow-md'
                      }
                      ${!isPast ? 'hover:scale-105 hover:-translate-y-1' : ''}
                    `}
                  >
                    {/* Day Number */}
                    <div className={`font-bold text-lg md:text-xl ${isToday ? 'text-green-700' : ''}`}>
                      {day}
                    </div>
                    
                    {/* Today Indicator */}
                    {isToday && (
                      <div className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></div>
                    )}
                    
                    {/* Booking Indicators */}
                    {hasBookings && (
                      <div className="mt-1 flex flex-col items-center">
                        <div className="text-xs font-medium">
                          {dayBookings.length} booking{dayBookings.length !== 1 ? 's' : ''}
                        </div>
                        <div className="flex gap-1 mt-1">
                          {dayBookings.map((booking, idx) => (
                            <div
                              key={idx}
                              className={`w-2 h-2 rounded-full ${
                                booking.field_type === 'rugby' ? 'bg-green-500' :
                                booking.field_type === 'sports' ? 'bg-orange-500' :
                                'bg-purple-500'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Hover Effect */}
                    {!isPast && (
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-green-400 opacity-0 group-hover:opacity-10 transition-opacity duration-200 rounded-xl"></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gradient-to-br from-green-100 to-green-200 border-2 border-green-300 rounded"></div>
                <span className="text-gray-600">Today</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300 rounded"></div>
                <span className="text-gray-600">Has Bookings</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Rugby Field</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-gray-600">Sports Field</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-gray-600">Both Fields</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[95vh] overflow-y-auto border border-gray-100">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-green-600 px-8 py-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">Book Your Field</h3>
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-blue-100 mt-2">Selected Date: {selectedDate}</p>
            </div>

            <div className="p-8">

              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Event Name
                  </label>
                  <input
                    type="text"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleInputChange}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="e.g., Rugby Training Session"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Field Type
                  </label>
                  <select
                    name="fieldType"
                    value={formData.fieldType}
                    onChange={handleInputChange}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    required
                  >
                    {getAvailableFieldTypes().map(type => (
                      <option key={type} value={type}>
                        {type === 'rugby' ? '🏉 Rugby Field' : 
                         type === 'sports' ? '⚽ Sports Field' : '🏟️ Both Fields'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Start Time
                    </label>
                    <input
                      type="time"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      End Time
                    </label>
                    <input
                      type="time"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="+27 123 456 789"
                    required
                  />
                </div>

                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="popiAccepted"
                      checked={formData.popiAccepted}
                      onChange={handleInputChange}
                      className="mt-1 w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500"
                      required
                    />
                    <label className="text-sm text-gray-700 leading-relaxed">
                      I accept the <strong>POPI (Protection of Personal Information)</strong> terms and conditions and consent to the processing of my personal information for booking purposes.
                    </label>
                  </div>
                </div>

                {/* reCAPTCHA Widget */}
                <div className="flex justify-center py-4">
                  <div ref={recaptchaRef}></div>
                </div>

                <div className="flex gap-4 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowBookingForm(false)}
                    className="flex-1 py-4 px-6 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-4 px-6 bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Booking...
                      </div>
                    ) : (
                      '🏟️ Book Field'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCalendar;
